import { MeshBuilder, StandardMaterial, Color3, Vector3 } from "@babylonjs/core";

/**
 * @class Enemy
 * @description Classe de base pour tous les ennemis. Définit les fondations communes pour le cycle de vie, le mesh et l'IA.
 *
 * Chaque type d'ennemi hérite de cette classe et surcharge :
 * - _createMesh() : Forme du mesh
 * - _applyMaterial() : Couleur et matériau
 * - think() : Comportement IA
 */
export class Enemy {
    /**
     * @param {import("@babylonjs/core").Scene} scene
     * @param {string} type - Identifiant du type d'ennemi
     * @param {import("@babylonjs/core").Vector3} startPosition
     */
    constructor(scene, type, startPosition) {
        /** @type {import("@babylonjs/core").Scene} */
        this.scene = scene;
        /** @type {string} */
        this.type = type;
        /** @type {boolean} */
        this.isDestroyed = false;
        /** @type {number} */
        this.hp = 1;      // Valeur par défaut — ÉCRASÉE par la classe fille
        /** @type {number} */
        this.baseHp = 1;  // PV de base avant application du hpMult (IA Edition)
        /** @type {import("@babylonjs/core").Mesh|null} */
        this.mesh = null;
    }

    /**
     * Initialise le mesh et les propriétés de base.
     * Doit être appelé à la fin du constructeur de chaque classe fille.
     * @param {import("@babylonjs/core").Vector3} startPosition
     * @protected
     */
    _initBase(startPosition) {
        this.mesh = this._createMesh();
        this.mesh.position = startPosition ? startPosition.clone() : new Vector3(0, 1, 0);
        this.mesh.metadata = { instance: this };
        this._applyMaterial();
        // Sauvegarder les PV de base une fois le mesh/HP initialisés par la classe fille
        this.baseHp = this.hp;
    }

    /**
     * Crée le mesh de l'ennemi.
     * À surcharger dans les classes filles.
     * @returns {import("@babylonjs/core").Mesh}
     * @protected
     */
    _createMesh() {
        return MeshBuilder.CreateSphere("base_enemy", { diameter: 1 }, this.scene);
    }

    /**
     * Applique le matériau de l'ennemi.
     * À surcharger dans les classes filles.
     * @protected
     */
    _applyMaterial() {
        const mat = new StandardMaterial("enemyMat", this.scene);
        mat.emissiveColor = new Color3(1, 0, 0); // Rouge par défaut
        if (this.mesh) this.mesh.material = mat;
    }

    /**
     * Exécute la logique de réflexion (IA) de l'ennemi.
     * À surcharger dans les classes filles.
     * @param {import("./Player.js").Player} player
     * @param {import("./EntityManager.js").EntityManager} entityManager
     * @param {import("../logic/ai/DataCollector.js").DataCollector} aiCollector
     */
    think(player, entityManager, aiCollector) {
        // Comportement de base (aucun)
    }

    /**
     * Active l'ennemi (sortie du pool) à une position donnée.
     * Remet les PV à baseHp (avant adaptation IA) — applyHpMult() sera rappelé si besoin.
     * @param {import("@babylonjs/core").Vector3} startPosition
     */
    activate(startPosition) {
        this.isDestroyed = false;
        this.hp = this.baseHp; // Reset PV de base (le hpMult sera réappliqué par EntityManager)
        if (this.mesh) {
            this.mesh.setEnabled(true);
            this.mesh.position = startPosition ? startPosition.clone() : new Vector3(0, 1, 0);
        }
    }

    /**
     * Désactive l'ennemi (retour au pool).
     */
    deactivate() {
        if (this.isDestroyed) return;
        this.isDestroyed = true;
        if (this.mesh) {
            this.mesh.setEnabled(false);
        }
    }

    /**
     * Inflige des dégâts à l'ennemi.
     * @param {number} amount - Quantité de dégâts (défaut: 1)
     * @returns {boolean} True si l'ennemi est mort (HP <= 0)
     */
    takeDamage(amount = 1) {
        this.hp -= amount;

        if (this.hp <= 0) {
            this.deactivate();
            return true;
        }

        return false;
    }

    /**
     * IA Edition — Applique un multiplicateur de PV au moment du spawn.
     * Appelé par EntityManager.spawnEnemy() juste après activation.
     * Ne modifie pas baseHp, seulement hp courant.
     * @param {number} mult - Multiplicateur (ex: 1.3 pour +30% PV)
     */
    applyHpMult(mult) {
        if (!mult || mult === 1) return;
        this.hp = Math.ceil(this.hp * mult);
    }

    /**
     * Détruit réellement l'objet et ses ressources (nettoyage mémoire).
     */
    dispose() {
        this.deactivate();
        if (this.mesh) {
            this.mesh.dispose();
            this.mesh = null;
        }
    }
}